import { FileRejection } from 'react-dropzone';
import { Label } from 'lib/Label';
import { FILE_SIZE_10MB } from 'modules/api/constants';
import { NotificationMessages } from 'consts/notifications';
import { ACCEPTED_FILE_TYPE_XLS } from 'consts/fileType';
import Chip, { Color, Width } from 'lib/Chip';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import Button, { Color as ButtonColor } from 'lib/Button';
import FileUpload from 'lib/FileUpload';
import { FileList } from 'components/common/types';
import { useDispatch } from 'react-redux';
import { addToastNotification } from 'store/notification/reducer';

const MAX_UPLOAD_FILES = 2;

interface Props {
  isReadOnly: boolean;
  listFiles: FileList[];
  onChangeListFiles: (listFiles: FileList[]) => void;
}

const ListUpload = ({
  isReadOnly = false,
  listFiles = [],
  onChangeListFiles = () => undefined,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();

  const onFileChange = (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
    if (rejectedFiles.length) {
      dispatch(
        addToastNotification({ description: NotificationMessages.INVALID_UPLOAD, type: 'Warning', timeout: 10000 }),
      );
      return;
    }

    const isDuplicateFile = acceptedFiles.find((file) => listFiles.find((item) => item.name === file.name));
    if (isDuplicateFile) {
      return;
    }

    const files: FileList[] = [...listFiles];
    acceptedFiles.forEach((file) => files.push({ include: true, file, name: file.name }));
    if (files.length > MAX_UPLOAD_FILES) {
      dispatch(addToastNotification({ description: NotificationMessages.MAX_FILE, type: 'Warning', timeout: 10000 }));
      return;
    }

    onChangeListFiles(files);
  };

  return (
    <div data-test-id="list-upload-container">
      <Label label="List upload" />
      <div className="mb-1.5">
        <FileUpload
          acceptFileType={ACCEPTED_FILE_TYPE_XLS}
          maxFileSize={FILE_SIZE_10MB}
          placeholder="Choose file to upload"
          onFileChange={onFileChange}
          isDisabled={isReadOnly}
        />
      </div>
      {listFiles.length ? (
        <div data-test-id="list-files-chips" className="flex flex-col gap-2 mt-2">
          {listFiles.map((item) => (
            <div key={item.name} aria-label={item.name} className="flex justify-between items-center">
              <Chip
                label={item.name}
                color={Color.PRIMARY}
                width={Width.FULL}
                truncateLabel={false}
                isDisabled={isReadOnly}
                onRemove={() => onChangeListFiles(listFiles.filter((file) => file.name !== item.name))}
              />
              <IncludeExcludeToggle
                option={{ code: item.name, name: item.name, include: item.include }}
                onIncludeExcludeChange={(updatedItem) => {
                  onChangeListFiles(
                    listFiles.map((file) =>
                      file.name === updatedItem.name ? { ...file, include: updatedItem.include } : file,
                    ),
                  );
                }}
                isAlwaysVisible
                isSelected={item.include !== undefined}
                isOptionNameSelectable={false}
                isHovered={false}
                isDeselectAllowed={false}
                isDisabled={isReadOnly}
              />
            </div>
          ))}
          {!isReadOnly && (
            <Button
              classNames="w-fit"
              label="Clear"
              color={ButtonColor.SECONDARY}
              onClick={() => onChangeListFiles([])}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ListUpload;
